import { CircleHelpIcon } from 'lucide-react';

import Brand from '@/components/atoms/Icons/Navigation/Brand';
import useMembers from '@/hooks/useMembers';

import { modelIconResolver, providerNameMapper } from './helper';
import { ResourceTile } from '../../components/ResourceList/ResourceTile';
import {
  ActiveStatus,
  CreatorMeta,
  LastUsedMeta,
  ResourceActiveSwitch,
  ResourceTileMeta,
} from '../../components/ResourceList/ResourceTileParts';
import { LanguageModel } from '../../types';

/** The provider's logo; Moveo models get the brand mark, an unknown provider a neutral glyph. */
const ProviderIcon = ({ provider }: { provider: LanguageModel['provider'] }) => {
  const logo = modelIconResolver(provider, 24);
  if (logo) {
    return logo;
  }
  if (provider === 'moveo' || provider === 'moveo-ai') {
    return <Brand size={24} color="var(--primary)" className="size-6 shrink-0" />;
  }
  return <CircleHelpIcon aria-hidden="true" className="size-6 text-muted-foreground" />;
};

interface LanguageModelItemProps {
  model: LanguageModel;
  isSelected: boolean;
  canWrite: boolean;
  onEdit: (model: LanguageModel) => void;
  onToggleEnabled: (model: LanguageModel) => void;
}

export const LanguageModelItem = ({
  model,
  isSelected,
  canWrite,
  onEdit,
  onToggleEnabled,
}: LanguageModelItemProps) => {
  const { getMember } = useMembers();
  const creator = model.created_by ? getMember(model.created_by) : undefined;
  // The logo shows the provider, so the title is the model alone. Screen readers can't see the
  // logo, so the switch label keeps the provider.
  const accessibleName = `${providerNameMapper(model.provider)} ${model.model}`;

  return (
    <ResourceTile
      icon={<ProviderIcon provider={model.provider} />}
      title={model.model}
      status={<ActiveStatus active={model.enabled} />}
      meta={
        <ResourceTileMeta>
          {creator && <CreatorMeta member={creator} />}
          <LastUsedMeta lastUsedAt={model.last_used_at} />
        </ResourceTileMeta>
      }
      toggle={
        <ResourceActiveSwitch
          name={accessibleName}
          checked={model.enabled}
          onCheckedChange={() => onToggleEnabled(model)}
          disabled={!canWrite}
        />
      }
      onOpen={() => onEdit(model)}
      isSelected={isSelected}
    />
  );
};
